import SearchIcon from "@mui/icons-material/Search";
import { Button, InputAdornment, Stack, TextField } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { round } from "../../utils/lodash-replacements";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useApi } from "../../containers/ApiContainer";
import { useSnackbar } from "../../containers/SnackbarContainer";
import { PredefinedObstacle } from "../../hooks/usePredefinedObstacle";
import { Hangar, Ramp, Stack as StackType } from "../../types";
import { uuidv4 } from "../../utils";

type Props = {
  movableObstacles: PredefinedObstacle[];
  location: Hangar | Ramp;
  stack: StackType;
  setStack: (stack: StackType) => void;
};

export const MovableObstacleGrid: React.FC<Props> = ({
  movableObstacles,
  location,
  stack,
  setStack,
}) => {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const [filter, setFilter] = React.useState<string>();

  const movableObstaclesToDisplay = React.useMemo(() => {
    if (!filter) {
      return movableObstacles;
    }
    return movableObstacles.filter(
      (mo) => mo.name.toLocaleLowerCase().indexOf(filter) > -1
    );
  }, [movableObstacles, filter]);

  const { activeFBO } = useActiveFBOs();
  const columns: GridColDef[] = [
    {
      field: "name",
      headerName: t("widgets.addMovableObstacle.movableObstacleGrid.columns.name"),
      width: 200,
      editable: false,
      sortable: false,
    },
    {
      field: "length",
      headerName: t("widgets.addMovableObstacle.movableObstacleGrid.columns.lengthFt"),
      width: 125,
      editable: false,
      sortable: false,
      valueFormatter: (value) => (value === 0 ? t("widgets.addMovableObstacle.movableObstacleGrid.notAvailable") : round(value, 2)),
    },
    {
      field: "width",
      headerName: t("widgets.addMovableObstacle.movableObstacleGrid.columns.widthFt"),
      width: 125,
      editable: false,
      sortable: false,
      valueFormatter: (value) => (value === 0 ? t("widgets.addMovableObstacle.movableObstacleGrid.notAvailable") : round(value, 2)),
    },
    {
      field: "height",
      headerName: t("widgets.addMovableObstacle.movableObstacleGrid.columns.heightFt"),
      width: 125,
      editable: false,
      sortable: false,
      valueFormatter: (value) => (value === 0 ? t("widgets.addMovableObstacle.movableObstacleGrid.notAvailable") : round(value, 2)),
    },
    {
      field: "action",
      headerName: "",
      sortable: false,
      width: 200,
      renderCell: (params) => {
        const snackbar = useSnackbar();
        return (
          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={async () => {
              const entity_id = uuidv4();
              const placement_id = uuidv4();

              const newObstacle = {
                id: uuidv4(),
                fbo_id: activeFBO.id,
                entity_id,
                obstacle_id: params.row.id,
                obstacle: params.row,
                selected: false,
                placement_id,
                position: {
                  id: placement_id,
                  stack_id: stack.id,
                  entity_id,
                  x: 0,
                  y: 0,
                  angle: 0,
                  preferences: [],
                },
              };

              // Save immediately to database
              try {
                await postgrest.from('movable_obstacle').upsert({
                  id: newObstacle.id,
                  fbo_id: newObstacle.fbo_id,
                  entity_id: newObstacle.entity_id,
                  obstacle_id: newObstacle.obstacle_id,
                });
                
                await postgrest.from('position').upsert({
                  id: newObstacle.position.id,
                  stack_id: stack.id, // Base stack ID
                  entity_id: newObstacle.entity_id,
                  x: newObstacle.position.x,
                  y: newObstacle.position.y,
                  angle: newObstacle.position.angle,
                  preferences: newObstacle.position.preferences || [],
                });

                // Update UI state for immediate display
                const newStack = {
                  ...stack,
                  movableObstacles: stack.movableObstacles.concat([newObstacle]),
                };

                setStack(newStack);

                snackbar.notify({
                  text: t("widgets.addMovableObstacle.movableObstacleGrid.messages.addSuccess", { obstacleName: params.row.name, locationName: location.name }),
                  severity: "success",
                });

              } catch (error: any) {
                console.error('Database save failed:', error);
                snackbar.notify({
                  text: t("widgets.addMovableObstacle.movableObstacleGrid.messages.addError", { obstacleName: params.row.name, errorMessage: error.message }),
                  severity: "error",
                });
              }
            }}
          >
            {t("widgets.addMovableObstacle.movableObstacleGrid.addButton")}
          </Button>
        );
      },
    },
  ];

  return (
    <Stack
      direction="column"
      spacing={1}
      sx={{ height: "100%", width: "100%" }}
    >
      <TextField
        fullWidth
        placeholder={t("widgets.addMovableObstacle.movableObstacleGrid.searchPlaceholder")}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        onChange={(evt) => setFilter(evt.target.value)}
      />
      <DataGrid
        disableColumnFilter
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnSelector
        rows={movableObstaclesToDisplay}
        columns={columns}
      />
    </Stack>
  );
};
